import { z } from 'zod'

/**
 * A consulta de CEP do cadastro de locais — e a regra que manda nela: ELA PODE
 * FALHAR. "CEP puxa endereço" é conveniência, nunca dependência: o serviço
 * público cai, fica lento e devolve endereço incompleto para loteamento novo.
 * Todo desfecho daqui é "achei", "não achei" ou "indisponível" — nunca uma
 * exceção que suba — e o formulário segue aberto para digitação nos três.
 *
 * A chamada é DIRETA ao ViaCEP, como no app (`apps/mobile/lib/funil/cep.ts`),
 * e não pelo servidor como no site. O site esconde do terceiro o CEP de cada
 * visitante; aqui o CEP é de um ponto da LOCADORA (aeroporto, loja), digitado
 * por quem trabalha nela — não há dado de pessoa a proteger. A CSP do painel
 * libera só esta origem em `connect-src` (`templates/seguranca.conf`).
 */

/** Teto de espera. Um CEP lento não pode segurar o cadastro. */
const TIMEOUT_EM_MS = 4_000

/**
 * O recorte do ViaCEP que interessa. `logradouro` e `bairro` vêm VAZIOS nos
 * CEPs de cidade inteira, comuns no interior — vazio significa "digite".
 */
const respostaDoViaCepSchema = z.object({
  logradouro: z.string().optional(),
  bairro: z.string().optional(),
  localidade: z.string().optional(),
  uf: z.string().optional(),
  /** Presente (`"true"`) quando o CEP não existe. O status HTTP continua 200. */
  erro: z.union([z.boolean(), z.string()]).optional(),
})

export interface EnderecoDoCep {
  readonly logradouro: string
  readonly bairro: string
  readonly cidade: string
  readonly uf: string
}

export type ResultadoDoCep =
  | { readonly situacao: 'ACHADO'; readonly endereco: EnderecoDoCep }
  | { readonly situacao: 'NAO_ENCONTRADO' }
  | { readonly situacao: 'INDISPONIVEL' }

/** Só os dígitos — o campo escreve a máscara, a consulta não a envia. */
export function digitosDoCep(valor: string): string {
  return valor.replace(/\D/g, '')
}

/** Consulta o CEP. Aceita com ou sem máscara; menos de oito dígitos é "não achei". */
export async function consultarCep(cepBruto: string): Promise<ResultadoDoCep> {
  const cep = digitosDoCep(cepBruto)

  if (cep.length !== 8) {
    return { situacao: 'NAO_ENCONTRADO' }
  }

  let resposta: Response

  try {
    resposta = await fetch(`https://viacep.com.br/ws/${cep}/json/`, {
      headers: { accept: 'application/json' },
      signal: AbortSignal.timeout(TIMEOUT_EM_MS),
    })
  } catch {
    return { situacao: 'INDISPONIVEL' }
  }

  if (!resposta.ok) {
    return resposta.status === 400 ? { situacao: 'NAO_ENCONTRADO' } : { situacao: 'INDISPONIVEL' }
  }

  const corpo: unknown = await resposta.json().catch(() => undefined)

  return interpretarViaCep(corpo)
}

/** A leitura da resposta, separada da rede — é o que a torna testável. */
export function interpretarViaCep(corpo: unknown): ResultadoDoCep {
  const analisado = respostaDoViaCepSchema.safeParse(corpo)

  if (!analisado.success) {
    return { situacao: 'INDISPONIVEL' }
  }

  const dados = analisado.data

  if (dados.erro !== undefined && dados.erro !== false && dados.erro !== 'false') {
    return { situacao: 'NAO_ENCONTRADO' }
  }

  // Sem cidade e sem UF não há o que preencher — e o resto viria pela metade.
  if (dados.localidade === undefined || dados.localidade.length === 0 || dados.uf?.length !== 2) {
    return { situacao: 'NAO_ENCONTRADO' }
  }

  return {
    situacao: 'ACHADO',
    endereco: {
      logradouro: dados.logradouro ?? '',
      bairro: dados.bairro ?? '',
      cidade: dados.localidade,
      uf: dados.uf.toUpperCase(),
    },
  }
}
